import React from 'react';
import {
  Badge, Button, EmptyState, IconButton, SkeletonRows, initialsOf,
} from '../../../components/app2';

/**
 * Administradores de una empresa (rediseño jul-2026).
 *
 * Mismas columnas y las mismas dos acciones por fila (restablecer contraseña y
 * eliminar), con el molde de tabla del sistema y tarjetas en móvil. Antes la
 * tabla se desbordaba en escritorio: la columna de acciones quedaba cortada y
 * había que hacer scroll horizontal para llegar a "Eliminar".
 */

const GRID = '38px minmax(170px,1.3fr) minmax(190px,1.3fr) 110px 148px 118px 84px';
const GRID_GAP = 12;
const GRID_MIN = 930;

const formatLastLogin = (lastLogin) => {
  if (!lastLogin) return 'Nunca';
  return new Date(lastLogin)?.toLocaleDateString('es-MX', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatCreated = (createdAt) => {
  if (!createdAt) return '—';
  return new Date(createdAt)?.toLocaleDateString('es-MX', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

const AdminUsersList = ({
  admins,
  selectedAdmins,
  isAllSelected,
  onSelectAll,
  onSelectAdmin,
  onDeleteAdmin,
  onResetPassword,
  loading,
  emptyStateProps,
}) => {
  if (loading) {
    return (
      <div style={{ padding: 20 }}>
        <SkeletonRows rows={4} title={false} />
      </div>
    );
  }

  if (!admins || admins.length === 0) {
    return (
      <EmptyState
        icon={emptyStateProps?.hasFilters ? 'person_search' : 'manage_accounts'}
        title={emptyStateProps?.hasFilters
          ? 'Sin coincidencias'
          : 'Esta empresa no tiene administradores'}
        text={emptyStateProps?.hasFilters
          ? 'Ajusta la búsqueda o el filtro de estado.'
          : `Sin un administrador, nadie de ${emptyStateProps?.companyName || 'la empresa'} puede sincronizar con el SAT ni gestionar su plan.`}
        action={emptyStateProps?.hasFilters
          ? null
          : (
            <Button icon="person_add" onClick={emptyStateProps?.onCreateNew}>
              Agregar el primero
            </Button>
          )}
        className="a2-card"
        style={{ border: 'none' }}
      />
    );
  }

  const Persona = ({ admin }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
      <span
        className="a2-avatar"
        style={{
          width: 38, height: 38, fontSize: '.76rem', flexShrink: 0,
          background: 'var(--surface-2)', border: '1px solid var(--line-2)', color: 'var(--text)',
        }}
      >
        {initialsOf(`${admin?.first_name || ''} ${admin?.last_name || ''}`)}
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          className="a2-tcell-strong a2-truncate"
          title={`${admin?.first_name || ''} ${admin?.last_name || ''}`.trim()}
        >
          {admin?.first_name} {admin?.last_name}
        </div>
        <div className="a2-tcell-muted a2-mono a2-truncate">
          {admin?.documentId || admin?.id}
        </div>
      </div>
    </div>
  );

  const Correo = ({ admin }) => (
    <div style={{ minWidth: 0 }}>
      <div className="a2-tcell a2-truncate" title={admin?.email}>{admin?.email}</div>
      {admin?.must_change_password ? (
        <Badge tone="warn" icon="key_off" style={{ marginTop: 4 }}>Debe cambiar contraseña</Badge>
      ) : null}
    </div>
  );

  const Estado = ({ admin }) => (
    <Badge
      tone={admin?.is_active ? 'ok' : 'neutral'}
      icon={admin?.is_active ? 'check_circle' : 'cancel'}
    >
      {admin?.is_active ? 'Activo' : 'Inactivo'}
    </Badge>
  );

  const Acciones = ({ admin }) => {
    const nombre = `${admin?.first_name || ''} ${admin?.last_name || ''}`.trim() || admin?.email;
    return (
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <IconButton
          icon="lock_reset"
          title={`Restablecer contraseña de ${nombre}`}
          aria-label={`Restablecer contraseña de ${nombre}`}
          onClick={() => onResetPassword?.(admin?.id)}
        />
        <IconButton
          icon="delete"
          tone="danger"
          title={`Eliminar a ${nombre}`}
          aria-label={`Eliminar a ${nombre}`}
          onClick={() => onDeleteAdmin?.(admin?.id)}
        />
      </div>
    );
  };

  const Casilla = ({ admin }) => (
    <label className="a2-check" title={`Seleccionar a ${admin?.first_name} ${admin?.last_name}`}>
      <input
        type="checkbox"
        checked={selectedAdmins?.includes(admin?.id) || false}
        onChange={() => onSelectAdmin?.(admin?.id)}
        aria-label={`Seleccionar a ${admin?.first_name} ${admin?.last_name}`}
      />
    </label>
  );

  return (
    <>
      {/* Escritorio */}
      <div className="a2-only-lg">
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: GRID_MIN }}>
            <div className="a2-thead" style={{ gridTemplateColumns: GRID, gap: GRID_GAP }}>
              <label className="a2-check" title="Seleccionar todos">
                <input
                  type="checkbox"
                  checked={Boolean(isAllSelected)}
                  onChange={onSelectAll}
                  aria-label="Seleccionar todos los administradores"
                />
              </label>
              <div>Administrador</div>
              <div>Correo</div>
              <div>Estado</div>
              <div>Último acceso</div>
              <div>Alta</div>
              <div style={{ textAlign: 'right' }}>Acciones</div>
            </div>

            {admins.map((admin) => (
              <div
                key={admin?.id}
                className="a2-trow"
                style={{
                  gridTemplateColumns: GRID,
                  gap: GRID_GAP,
                  background: selectedAdmins?.includes(admin?.id) ? 'var(--brand-soft)' : undefined,
                }}
              >
                <Casilla admin={admin} />
                <Persona admin={admin} />
                <Correo admin={admin} />
                <div><Estado admin={admin} /></div>
                <div className="a2-tcell">{formatLastLogin(admin?.last_login)}</div>
                <div className="a2-tcell">{formatCreated(admin?.createdAt)}</div>
                <Acciones admin={admin} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Móvil */}
      <div className="a2-only-sm">
        {admins.map((admin) => (
          <div
            key={admin?.id}
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid var(--line)',
              background: selectedAdmins?.includes(admin?.id) ? 'var(--brand-soft)' : undefined,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ paddingTop: 10 }}><Casilla admin={admin} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Persona admin={admin} />
                <div style={{ marginTop: 8 }}><Correo admin={admin} /></div>
              </div>
              <Estado admin={admin} />
            </div>

            <div
              style={{
                marginTop: 12, display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10,
              }}
            >
              <div>
                <div className="a2-tcell-muted">Último acceso</div>
                <div className="a2-tcell">{formatLastLogin(admin?.last_login)}</div>
              </div>
              <div>
                <div className="a2-tcell-muted">Alta</div>
                <div className="a2-tcell">{formatCreated(admin?.createdAt)}</div>
              </div>
            </div>

            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <Acciones admin={admin} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default AdminUsersList;
